/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2026 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { forwardRef, ReactNode } from "react";
import {
  Badge as BootstrapBadge,
  BadgeProps as BootstrapBadgeProps,
} from "react-bootstrap";

export type BadgeVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "light"
  | "dark"
  | "outline";

export type BadgeShape = "default" | "pill" | "dot";

export type BadgeSize = "sm" | "md" | "lg";

export interface BadgeProps
  extends Omit<BootstrapBadgeProps, "bg" | "pill" | "children"> {
  variant?: BadgeVariant;
  /** Default (rounded corners), Pill or Dot (circle without label) */
  shape?: BadgeShape;
  /** Small (22px, default), Medium (28px) or Large (28px, 14px text) */
  size?: BadgeSize;
  icon?: ReactNode;
  label?: ReactNode;
}

/**
 * Design System badge: Bootstrap badge with the branding styles, sizes, icon and dot support.
 * Icon-only and dot badges have no visible text, so pass `aria-label` or `title`.
 */
export const Badge = forwardRef<HTMLElement, BadgeProps>(
  (
    {
      variant = "primary",
      shape = "default",
      size = "sm",
      icon,
      label,
      className = "",
      ...rest
    },
    ref
  ) => {
    const isDot = shape === "dot";
    const hasIcon = !isDot && !!icon;
    const hasLabel = !isDot && label !== undefined && label !== null;

    const classes = [
      variant === "outline" && "badge-outline",
      size !== "sm" && `badge-${size}`,
      isDot && "badge-dot",
      hasIcon && "badge-with-icon",
      hasIcon && !hasLabel && "badge-icon-only",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <BootstrapBadge
        ref={ref}
        bg={variant === "outline" ? "" : variant}
        pill={shape === "pill"}
        className={classes}
        {...rest}
      >
        {hasIcon && <span className="badge-icon">{icon}</span>}
        {hasLabel && label}
      </BootstrapBadge>
    );
  }
);

Badge.displayName = "Badge";
